import React, { useRef, useState } from 'react';
import { ImageIcon, Upload, X } from 'lucide-react';

const LABEL_CLASS = 'block text-sm font-medium text-black mb-2 font-["Open_Sans"]';
const MAX_SIZE_MB = 5;

const LostAndFoundImageUpload = ({
  label = 'Upload image',
  value,
  onChange,
  onFileSelect,
  onValidationError,
  emptyTitle = 'Add a photo',
  emptyHint = 'Drag and drop here, or browse from your device',
  className = '',
  compact = false,
}) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const processFile = file => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onValidationError?.('Please upload an image file (PNG, JPG, etc.)');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      onValidationError?.(`Image must be smaller than ${MAX_SIZE_MB} MB`);
      return;
    }
    onFileSelect?.(file);
    const reader = new FileReader();
    reader.onload = () => {
      onChange?.(reader.result);
      onValidationError?.('');
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = e => {
    processFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragOver(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const handleClear = () => {
    onChange?.(null);
    onFileSelect?.(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openPicker = () => fileInputRef.current?.click();

  return (
    <div className={className}>
      <span className={LABEL_CLASS}>{label}</span>

      <div
        className={`w-full rounded-2xl border transition-colors ${
          compact ? 'max-w-[200px] p-3' : 'max-w-sm p-4'
        } ${
          isDragOver
            ? 'border-[#6BABA0] bg-[#6BABA0]/5'
            : compact
              ? 'border-zinc-200 bg-gray-50/40'
              : 'border-zinc-200 bg-gradient-to-b from-gray-50/80 to-white'
        }`}
        onDrop={handleDrop}
        onDragOver={e => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
      >
        <div
          className={`relative aspect-square w-full overflow-hidden rounded-xl border shadow-inner ${
            compact ? 'bg-gray-50/50' : 'bg-white'
          } ${value ? 'border-zinc-200' : 'border-dashed border-zinc-300'}`}
        >
          {value ? (
            <>
              <img src={value} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <button
                type="button"
                onClick={handleClear}
                className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow-md hover:bg-white hover:text-red-600 transition-colors"
                aria-label="Remove image"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={openPicker}
              className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center hover:bg-[#6BABA0]/5 transition-colors"
            >
              <div
                className={`flex items-center justify-center rounded-2xl bg-[#6BABA0]/10 text-[#6BABA0] ${
                  compact ? 'h-10 w-10' : 'h-14 w-14'
                }`}
              >
                <ImageIcon className={compact ? 'h-5 w-5' : 'h-7 w-7'} strokeWidth={1.5} />
              </div>
              {!compact && (
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-gray-800 font-['Open_Sans']">
                    {emptyTitle}
                  </span>
                  <span className="text-xs text-gray-500 font-['Open_Sans'] leading-relaxed">
                    {emptyHint}
                  </span>
                </div>
              )}
              {compact && (
                <span className="text-xs font-medium text-gray-600 font-['Open_Sans']">
                  {emptyTitle}
                </span>
              )}
            </button>
          )}
        </div>

        <div className={`flex flex-wrap gap-2 ${compact ? 'mt-2' : 'mt-4'}`}>
          <button
            type="button"
            onClick={openPicker}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-[#6BABA0] bg-white font-medium text-[#6BABA0] font-['Open_Sans'] hover:bg-[#6BABA0]/10 transition-colors ${
              compact ? 'px-2.5 py-1.5 text-xs' : 'px-4 py-2.5 text-sm gap-2 rounded-xl'
            }`}
          >
            <Upload className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
            {value ? 'Change' : compact ? 'Browse' : 'Browse files'}
          </button>
          {value && !compact && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 font-['Open_Sans'] hover:bg-gray-50 transition-colors"
            >
              Remove
            </button>
          )}
        </div>

        {!compact && (
          <p className="mt-2 text-xs text-gray-400 font-['Open_Sans']">
            PNG or JPG · max {MAX_SIZE_MB} MB
          </p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default LostAndFoundImageUpload;
